import type { Category, Todo } from "../../domain/entities.ts";
import { renderMarkdown } from "../../ui/markdown.ts";
import { escapeHtml } from "../../ui/text.ts";
import { commentBody } from "../collaboration/comment-view.ts";
import { internalKey, internalTargets, markdownInternalLink, referencedKeys, type InternalTarget } from "./internal-links.ts";

interface Capabilities {
  document: Document;
  tasks(): Todo[];
  categories(): Category[];
  roots?(): { id: string; name: string }[];
  selectedTask(): string | null;
  location(): string;
  openTask(id: string, commentId?: string): Promise<void>;
  openReference(id: string, section?: string): Promise<void>;
  closeReference(): Promise<void>;
  toast(message: string): void;
}

export function createInternalLinksController(c: Capabilities) {
  let dialog: HTMLDialogElement | null = null;
  const targets = () => internalTargets(c.tasks(), c.categories(), c.roots?.());

  function createDialog(label: string): HTMLDialogElement {
    dialog?.close();
    const element = c.document.createElement("dialog");
    element.className = "internal-link-dialog";
    element.setAttribute("aria-label", label);
    // Keep shell shortcuts (including Escape and Ctrl+Enter) inside this layer.
    element.addEventListener("keydown", event => event.stopPropagation());
    const previous = c.document.activeElement as HTMLElement | null;
    element.addEventListener("close", () => { element.remove(); if (dialog === element) dialog = null; if (previous?.isConnected) previous.focus(); });
    c.document.body.append(element);
    dialog = element;
    return element;
  }

  async function open(target: InternalTarget) {
    // Resolve again: grants or content may have changed while the dialog was open.
    if (!targets().some(item => item.key === target.key)) { c.toast("This reference is unavailable"); return; }
    const url = new URL(target.href, c.location());
    if (target.kind === "Task") {
      await c.closeReference();
      await c.openTask(url.searchParams.get("task")!, url.searchParams.get("comment") || undefined);
    } else await c.openReference(url.searchParams.get("collection")!, url.searchParams.get("note") || undefined);
  }

  function preview(key: string, href?: string) {
    let target = targets().find(item => item.key === key);
    if (!target) { c.toast("This reference is unavailable on this device"); return; }
    if (href && internalKey(href, c.location()) === key) target = { ...target, href };
    const element = createDialog("Reference preview");
    element.innerHTML = `<h2>${escapeHtml(target.title)}</h2><small>${target.kind} · ${escapeHtml(target.context || "")}</small><p class="internal-link-preview">${escapeHtml(target.preview || "No description")}</p><div class="dialog-actions"><button type="button" data-link-close class="button button--soft">Close</button><button type="button" data-link-open class="button button--primary">Open ${target.kind === "Task" ? "task" : "reference"}</button></div>`;
    element.querySelector("[data-link-close]")!.addEventListener("click", () => element.close());
    element.querySelector("[data-link-open]")!.addEventListener("click", () => { element.close(); void open(target).catch(() => c.toast("Could not open the reference")); });
    element.showModal();
  }

  function picker(input: HTMLTextAreaElement) {
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const element = createDialog("Insert internal link");
    element.innerHTML = `<h2>Link to work or context</h2><label>Find a task or reference<input type="search" data-link-search autocomplete="off" placeholder="Search titles, spaces or collections…"></label><p class="field-note" data-link-count role="status"></p><div class="internal-link-results" data-link-results></div><button type="button" class="text-button" data-link-more hidden>Show more results</button><div class="dialog-actions"><button type="button" class="button button--soft" data-link-close>Cancel</button></div>`;
    const search = element.querySelector<HTMLInputElement>("[data-link-search]")!;
    const results = element.querySelector<HTMLElement>("[data-link-results]")!;
    let limit = 30;
    function update() {
      const words = search.value.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
      const all = targets().filter(target => words.every(word => `${target.title} ${target.context || ""} ${target.kind}`.toLocaleLowerCase().includes(word)));
      const matches = all.slice(0, limit);
      element.querySelector("[data-link-count]")!.textContent = all.length ? `${matches.length} of ${all.length} results available on this device` : "";
      element.querySelector<HTMLButtonElement>("[data-link-more]")!.hidden = matches.length >= all.length;
      results.innerHTML = matches.length ? matches.map((target, index) => `<button type="button" data-link-choice="${index}"><span>${escapeHtml(target.title)}</span><small>${target.kind} · ${escapeHtml(target.context || "")}</small></button>`).join("") : "<p>No matching tasks or references available on this device.</p>";
      results.querySelectorAll<HTMLButtonElement>("[data-link-choice]").forEach(button => button.addEventListener("click", () => {
        const target = matches[Number(button.dataset.linkChoice)];
        if (!input.isConnected || !targets().some(item => item.key === target.key)) { element.close(); return; }
        const value = markdownInternalLink(target);
        if (input.maxLength >= 0 && input.value.length - (end - start) + value.length > input.maxLength) { c.toast("The link exceeds this field’s length limit"); return; }
        input.setRangeText(value, start, end, "end");
        input.dispatchEvent(new c.document.defaultView!.Event("input", { bubbles: true }));
        element.close();
        input.focus();
      }));
    }
    search.addEventListener("input", () => { limit = 30; update(); });
    element.querySelector("[data-link-more]")!.addEventListener("click", () => { const first = limit; limit += 30; update(); results.querySelectorAll<HTMLElement>("button")[first]?.focus(); });
    element.querySelector("[data-link-close]")!.addEventListener("click", () => element.close());
    update(); element.showModal(); search.focus();
  }

  function backlinks(key: string): string {
    const sources = new Map<string, InternalTarget>();
    const available = targets();
    const matches = (source: string, render = renderMarkdown) => Array.from(referencedKeys(source, c.location(), render, c.document)).some(found => found === key || (key.startsWith("collection:") && key.split(":").length === 2 && found.startsWith(`${key}:`)));
    for (const task of c.tasks().filter(task => !task.deleted && !task.archivedAt)) {
      if (matches(task.description) || (task.comments || []).some(comment => !comment.deletedAt && matches(comment.body, commentBody))) {
        const source = available.find(target => target.key === `task:${task.id}`);
        if (source && source.key !== key) sources.set(source.key, source);
      }
    }
    for (const category of c.categories().filter(category => !category.deleted && !category.archivedAt)) {
      if (matches(category.referenceNote || "")) {
        const source = available.find(target => target.key === `collection:${category.id}`);
        if (source && source.key !== key) sources.set(source.key, source);
      }
    }
    return sources.size ? `<details class="internal-backlinks"><summary>Referenced by · ${sources.size}</summary>${Array.from(sources.values()).map(source => `<button type="button" class="text-button" data-internal-preview="${escapeHtml(source.key)}">${escapeHtml(source.title)} <small>${source.kind}</small></button>`).join("")}</details>` : "";
  }

  function bind(root: ParentNode = c.document) {
    const available = targets();
    root.querySelectorAll<HTMLAnchorElement>(".markdown-body a[href], .comment-thread a[href]").forEach(anchor => {
      const key = internalKey(anchor.getAttribute("href")!, c.location());
      if (!key) return;
      const target = available.find(item => item.key === key);
      anchor.title = target ? `${target.kind}: ${target.title}\n${target.preview}\nActivate to preview` : "Reference unavailable on this device";
    });
    root.querySelectorAll<HTMLTextAreaElement>('textarea[name="description"], #comment-form textarea[name="body"], textarea[data-reference-input]').forEach(input => {
      if (input.readOnly || input.disabled || input.nextElementSibling?.hasAttribute("data-internal-insert")) return;
      const button = c.document.createElement("button");
      button.type = "button"; button.className = "text-button internal-link-insert";
      button.setAttribute("data-internal-insert", ""); button.textContent = "Insert link";
      button.addEventListener("click", () => picker(input)); input.after(button);
    });
    const taskHost = root.querySelector<HTMLElement>(".task-view__body");
    const referenceHost = root.querySelector<HTMLElement>(".collection-reference-panel__scroll");
    const selected = c.selectedTask();
    for (const [host, key] of [[taskHost, selected ? `task:${selected}` : null], [referenceHost, referenceHost?.closest<HTMLElement>("[data-collection-reference]")?.dataset.collectionReference ? `collection:${referenceHost.closest<HTMLElement>("[data-collection-reference]")!.dataset.collectionReference}` : null]] as const) {
      if (!host || !key) continue;
      let slot = host.querySelector<HTMLElement>("[data-internal-backlinks]");
      if (!slot) { slot = c.document.createElement("div"); slot.dataset.internalBacklinks = ""; host.append(slot); }
      slot.innerHTML = backlinks(key);
    }
  }

  c.document.addEventListener("click", event => {
    const element = event.target as Element | null;
    if (!element?.closest) return;
    const button = element?.closest<HTMLElement>("[data-internal-preview]");
    const anchor = element?.closest<HTMLAnchorElement>(".markdown-body a[href], .comment-thread a[href]");
    if (!button && !anchor) return;
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const key = button?.dataset.internalPreview || (anchor ? internalKey(anchor.getAttribute("href")!, c.location()) : null);
    if (!key) return;
    event.preventDefault(); event.stopPropagation(); preview(key, anchor?.getAttribute("href") || undefined);
  }, true);

  return { bind };
}
